import { useMemo } from 'react';
import { Button } from 'antd-mobile';
import { Icon } from '@fuxi/eevee-icon';
import CloseIcon from '@/assets/icon/DrawerClose.svg';
import bodyPostureImg from '@/assets/image/body-posture.png';
import bodyPostureStayingImg from '@/assets/image/body-posture-staying.png';
import BodyDetection2DViewer, { BodyDetection2DViewerProp } from '@/components/body-detection-2d-viewer';
import { RobotExceptionEventsHandler } from '@/components/robot-exception-prompt/utils';
import { SuggestionsForFeedback } from '@/components';
import cx from './index.module.scss';

export interface AnomalyDetectCardRefAttrs {
  show: VoidFunc;
  hide: VoidFunc;
}

type Props = {
  handleClose: VoidFunc;
  currentErrorName: MassageRuntime.RobotExceptionEvents;
  isDetectingTimeout: boolean;
  endService: () => Promise<boolean>;
  handleRetryHumanDetecting: () => Promise<void>;
  barePosition?: BarePositionType;
  isLightAbnormal: boolean;
  currentStep: Service.SubStep;
} & BodyDetection2DViewerProp;

const DialogFrame = ({ text = '' }: { text: string }) => {
  return (
    <div className={cx('dialog')}>
      <p>{text + '，请调整后，再试一次。'}</p>
    </div>
  );
};

const AnomalyDetectModal = ({
  handleClose,
  part,
  points,
  imgUrl,
  onImageLoaded,
  currentErrorName,
  endService,
  handleRetryHumanDetecting,
  isDetectingTimeout,
  barePosition,
  isLightAbnormal,
  currentStep,
}: Props) => {
  const dialogTextList = useMemo(() => {
    let list =
      points?.map((point, index) => ({
        key: point.noduleId,
        text: `${RobotExceptionEventsHandler[currentErrorName]?.bodyAbnormalTips}`,
      })) || [];

    //Note 使用的是理疗区域过小的异常，但是当上下遮挡箭头都为绿色的时候，需要之前提示，并提示遮挡
    if (barePosition?.bottom?.pass === 'True' && barePosition?.top?.pass === 'True') {
      list = [];
      list.push({
        key: `detected-shelter`,
        text: `检测到理疗区域有遮挡`,
      });
    }

    // 此字段会添加到所有异常中因此需要单独判断
    if (isLightAbnormal) {
      list.push({
        key: `detected-shelter`,
        text: `检测到理疗区域光照异常`,
      });
    }

    if (currentErrorName === 'DETECTING_POSE_ERROR') {
      list.push({
        key: `DETECTING_POSE_ERROR`,
        text: part === 'Abdomen' ? '检测到顾客姿势未躺正' : '检测到顾客姿势未趴正',
      });
    }

    if (currentErrorName === 'DETECTING_NOT_STAYING') {
      list.push({
        key: `DETECTING_NOT_STAYING`,
        text: `检测到身体移动幅度过大`,
      });
    }

    return list;
  }, [barePosition, currentErrorName, isLightAbnormal, part, points]);

  return (
    <div className={cx['wrapper']}>
      <div className={cx('header')}>
        <div className={cx('title-box')}>
          <div className={cx('title')}>人体检测异常</div>
          <div className={cx('tips')}>
            <Icon name="tips_R1pBle" className={cx('icon')} />
            <span>请根据异常画面提示调整</span>
          </div>
        </div>

        <img src={CloseIcon} className={cx('close-icon')} alt="drawer-close" onClick={() => handleClose()} />
      </div>

      <div className={cx('img-content')}>
        <div className={cx('canvas-container')}>
          <BodyDetection2DViewer part={part} points={points} imgUrl={imgUrl} onImageLoaded={onImageLoaded} />
          {currentErrorName === 'DETECTING_POSE_ERROR' && (
            <img className={cx('body-posture')} alt="body-posture" src={bodyPostureImg}></img>
          )}
          {currentErrorName === 'DETECTING_NOT_STAYING' && (
            <img className={cx('body-posture')} alt="body-posture" src={bodyPostureStayingImg}></img>
          )}
        </div>

        <div className={cx('anomaly-list')}>
          {dialogTextList?.map(({ key, text }, index) => (
            <DialogFrame key={key} text={`${index + 1}.${text}`} />
          ))}
        </div>
      </div>

      {isDetectingTimeout && (
        <div className={cx('retry-box')}>
          <Button onClick={endService}>结束服务</Button>
          <Button className={cx('retry-btn')} onClick={handleRetryHumanDetecting}>
            再次检测
          </Button>
        </div>
      )}
      {!isDetectingTimeout && (
        <SuggestionsForFeedback
          currentProcess={currentStep === 'Massage' ? 'massage-visual-error' : 'visual-error'}
          className={cx('feedback-box')}
        />
      )}
    </div>
  );
};

export default AnomalyDetectModal;
